
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../../lib/api';

const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing reset token. Please request a new link.');
        }
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await apiClient.resetPassword(token, password);
            setSuccess(true);
            setTimeout(() => {
                navigate('/admin');
            }, 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to reset password. The link may have expired.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen luxury-gradient flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-luxury p-8 w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-lock-password-line text-primary-500 text-3xl"></i>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Set New Password</h1>
                    <p className="text-gray-600">Please enter your new secure password</p>
                </div>

                {success ? (
                    <div className="text-center space-y-6">
                        <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg flex items-center justify-center space-x-2">
                            <i className="ri-checkbox-circle-line text-xl"></i>
                            <span>Password reset successfully!</span>
                        </div>
                        <p className="text-gray-600">Redirecting you to login page...</p>
                        <Link
                            to="/admin"
                            className="inline-block bg-primary-400 hover:bg-primary-500 text-black px-8 py-3 rounded-lg font-bold transition-all duration-300"
                        >
                            Go to Login Now
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg text-sm flex items-center space-x-2">
                                <i className="ri-error-warning-line text-lg"></i>
                                <span>{error}</span>
                            </div>
                        )}

                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">New Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                                placeholder="Min 6 characters"
                                required
                                disabled={loading || !token}
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 font-semibold mb-2">Confirm New Password</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                                placeholder="Confirm your password"
                                required
                                disabled={loading || !token}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !token}
                            className="w-full bg-primary-400 hover:bg-primary-500 text-black py-4 rounded-lg font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-lg"
                        >
                            {loading ? (
                                <>
                                    <i className="ri-loader-line animate-spin mr-2"></i>
                                    Resetting Password...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </button>

                        <div className="text-center">
                            <Link to="/admin" className="text-gray-500 hover:text-gray-700 text-sm transition-colors">
                                Back to Login
                            </Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ResetPasswordPage;
